/**
 * Admin service for handling admin operations
 */

import axiosClient from "../../config/axiosClient";
import { ENDPOINTS } from "../../config/endpoints";
import { CreateUserData } from "./admin.types";
import { extractApiErrorMessage, showUploadErrorAlert } from "../../helpers/apiError";

export interface PendingUser {
  id: string;
  auth0Id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  role: string;
  isActive: boolean;
  companyId: string;
  allowedModules?: string[];
  createdAt: Date;
  updatedAt: Date;
  company: {
    id: string;
    name: string;
    email: string;
    approvalStatus: string;
    approvedAt: Date | null;
  };
}

export interface AdminResponse {
  success: boolean;
  data: any;
  message?: string;
}

class AdminService {
  // Request cache for deduplication
  private requestCache = new Map<string, Promise<any>>();
  private cacheTimestamps = new Map<string, number>();
  private readonly CACHE_DURATION = 30 * 1000; // 30 seconds - shorter for more responsive updates

  /**
   * Generate cache key for requests
   */
  private getCacheKey(endpoint: string, params?: any): string {
    return `${endpoint}:${JSON.stringify(params || {})}`;
  }

  /**
   * Check if cache is still valid
   */
  private isCacheValid(key: string): boolean {
    const timestamp = this.cacheTimestamps.get(key);
    if (!timestamp) return false;
    return Date.now() - timestamp < this.CACHE_DURATION;
  }

  /**
   * Clear expired cache entries
   */
  private clearExpiredCache(): void {
    const now = Date.now();
    for (const [key, timestamp] of this.cacheTimestamps.entries()) {
      if (now - timestamp >= this.CACHE_DURATION) {
        this.requestCache.delete(key);
        this.cacheTimestamps.delete(key);
      }
    }
  }

  /**
   * Invalidate categories cache
   */
  private invalidateCategoriesCache(): void {
    // Clear all category-related cache entries (both public and admin endpoints)
    for (const [key] of this.requestCache.entries()) {
      if (
        key.includes(ENDPOINTS.ADMIN.CATEGORIES) ||
        key.includes(ENDPOINTS.CATEGORIES.BASE)
      ) {
        this.requestCache.delete(key);
        this.cacheTimestamps.delete(key);
      }
    }
  }

  /**
   * Get all users with their status (enhanced with pagination, filters, sorting)
   */
  async getAllUsers(queryParams?: any): Promise<{
    data: PendingUser[];
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const response = await axiosClient.get<{
      success: boolean;
      data: {
        data: PendingUser[];
        pagination?: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      };
    }>(ENDPOINTS.ADMIN.ALL_USERS, { params: queryParams });

    // Handle both old format (array) and new format (object with pagination)
    if (Array.isArray(response.data)) {
      return { data: response.data };
    }

    // Extract data from the nested structure (TransformInterceptor wraps it)
    if (response.data?.data) {
      return response.data.data;
    }

    // Fallback: if data is directly an array
    if (Array.isArray(response.data?.data)) {
      return { data: response.data.data };
    }

    // Last fallback
    return { data: [] };
  }

  /**
   * Get pending users for approval
   */
  async getPendingUsers(): Promise<PendingUser[]> {
    const response = await axiosClient.get<{
      success: boolean;
      data: PendingUser[];
    }>(ENDPOINTS.ADMIN.PENDING_USERS);
    return response.data.data;
  }

  /**
   * Approve a user
   */
  async approveUser(userId: string): Promise<AdminResponse> {
    const response = await axiosClient.put<AdminResponse>(
      ENDPOINTS.ADMIN.APPROVE_USER(userId)
    );
    return response.data;
  }

  /**
   * Reject a user
   */
  async rejectUser(userId: string): Promise<AdminResponse> {
    const response = await axiosClient.put<AdminResponse>(
      ENDPOINTS.ADMIN.REJECT_USER(userId)
    );
    return response.data;
  }

  /**
   * Get database statistics
   */
  async getDatabaseStats(): Promise<AdminResponse> {
    const response = await axiosClient.post<AdminResponse>(
      ENDPOINTS.ADMIN.DATABASE_STATS
    );
    return response.data;
  }

  /**
   * Clear all data from database
   */
  async clearAllData(): Promise<AdminResponse> {
    const response = await axiosClient.delete<AdminResponse>(
      ENDPOINTS.ADMIN.CLEAR_ALL_DATA
    );
    return response.data;
  }

  /**
   * Reset database to initial state
   */
  async resetDatabase(): Promise<AdminResponse> {
    const response = await axiosClient.post<AdminResponse>(
      ENDPOINTS.ADMIN.RESET_DATABASE
    );
    return response.data;
  }

  /**
   * Create a new user (Admin only)
   */
  async createUser(userData: CreateUserData): Promise<AdminResponse> {
    const response = await axiosClient.post<AdminResponse>(
      ENDPOINTS.ADMIN.CREATE_USER,
      userData
    );
    return response.data;
  }

  /**
   * Get user by ID
   */
  async getUserById(userId: string): Promise<PendingUser> {
    // Validate userId is not a placeholder
    if (!userId || userId === ":id" || userId.trim() === "") {
      throw new Error("Invalid user ID provided");
    }

    const response = await axiosClient.get<{
      success: boolean;
      data: PendingUser;
    }>(ENDPOINTS.ADMIN.USER_BY_ID(userId));
    return response.data.data;
  }

  /**
   * Update user
   */
  async updateUser(userId: string, userData: any): Promise<AdminResponse> {
    const response = await axiosClient.put<AdminResponse>(
      ENDPOINTS.ADMIN.UPDATE_USER(userId),
      userData
    );
    return response.data;
  }

  /**
   * Delete user (soft delete)
   */
  async deleteUser(userId: string): Promise<AdminResponse> {
    const response = await axiosClient.delete<AdminResponse>(
      ENDPOINTS.ADMIN.DELETE_USER(userId)
    );
    return response.data;
  }

  /**
   * Suspend user
   */
  async suspendUser(userId: string): Promise<AdminResponse> {
    const response = await axiosClient.put<AdminResponse>(
      ENDPOINTS.ADMIN.SUSPEND_USER(userId)
    );
    return response.data;
  }

  /**
   * Activate user
   */
  async activateUser(userId: string): Promise<AdminResponse> {
    const response = await axiosClient.put<AdminResponse>(
      ENDPOINTS.ADMIN.ACTIVATE_USER(userId)
    );
    return response.data;
  }

  /**
   * Update user role
   */
  async updateUserRole(userId: string, role: string): Promise<AdminResponse> {
    const response = await axiosClient.put<AdminResponse>(
      ENDPOINTS.ADMIN.UPDATE_USER_ROLE(userId),
      { role }
    );
    return response.data;
  }

  /**
   * Reset user password
   */
  async resetUserPassword(
    userId: string,
    options?: { newPassword?: string; sendEmail?: boolean }
  ): Promise<AdminResponse> {
    const response = await axiosClient.post<AdminResponse>(
      ENDPOINTS.ADMIN.RESET_USER_PASSWORD(userId),
      options || {}
    );
    return response.data;
  }

  /**
   * Bulk user operations
   */
  async bulkUserAction(
    userIds: string[],
    action: "approve" | "reject" | "delete" | "activate" | "suspend",
    reason?: string
  ): Promise<AdminResponse> {
    const response = await axiosClient.post<AdminResponse>(
      ENDPOINTS.ADMIN.BULK_USER_ACTION,
      {
        userIds,
        action,
        reason,
      }
    );
    return response.data;
  }

  /**
   * Export users to CSV
   */
  async exportUsers(queryParams?: any): Promise<Blob> {
    const response = await axiosClient.get(ENDPOINTS.ADMIN.EXPORT_USERS, {
      params: queryParams,
      responseType: "blob",
    });
    return response.data;
  }

  /**
   * Get user statistics
   */
  async getUserStatistics(): Promise<{
    total: number;
    active: number;
    inactive: number;
    pending: number;
    recentRegistrations: number;
    byRole: Record<string, number>;
  }> {
    const response = await axiosClient.get<{
      success: boolean;
      data: {
        total: number;
        active: number;
        inactive: number;
        pending: number;
        recentRegistrations: number;
        byRole: Record<string, number>;
      };
    }>(ENDPOINTS.ADMIN.USER_STATISTICS);
    return response.data.data;
  }

  /**
   * Get dashboard statistics
   */
  async getDashboardStatistics(): Promise<{
    totalUsers: number;
    activeSellers: number;
    totalProducts: number;
    changes: {
      users: { value: number; isPositive: boolean };
      sellers: { value: number; isPositive: boolean };
      products: { value: number; isPositive: boolean };
    };
    timestamp: string;
  }> {
    const response = await axiosClient.get<{
      success: boolean;
      data: {
        totalUsers: number;
        activeSellers: number;
        totalProducts: number;
        changes: {
          users: { value: number; isPositive: boolean };
          sellers: { value: number; isPositive: boolean };
          products: { value: number; isPositive: boolean };
        };
        timestamp: string;
      };
    }>(ENDPOINTS.ADMIN.DASHBOARD_STATISTICS);
    return response.data.data;
  }

  /**
   * Get recent activity for dashboard
   */
  async getRecentActivity(): Promise<
    Array<{
      id: string;
      type: string;
      user: string;
      action: string;
      timestamp: string;
      status: string;
      profileImage?: string | null;
      companyLogo?: string | null;
    }>
  > {
    const response = await axiosClient.get<{
      success: boolean;
      data: Array<{
        id: string;
        type: string;
        user: string;
        action: string;
        timestamp: string;
        status: string;
        profileImage?: string | null;
        companyLogo?: string | null;
      }>;
    }>(ENDPOINTS.ADMIN.DASHBOARD_RECENT_ACTIVITY);
    return response.data.data;
  }

  /**
   * Create a new product as admin
   */
  async createProductAsAdmin(productData: any): Promise<AdminResponse> {
    const response = await axiosClient.post<AdminResponse>(
      ENDPOINTS.ADMIN.PRODUCTS,
      productData
    );
    return response.data;
  }

  /**
   * Get all admin products with filters
   */
  async getAdminProducts(
    filters?: any,
    forceRefresh = false
  ): Promise<AdminResponse> {
    // Clear expired cache entries
    this.clearExpiredCache();

    const cacheKey = this.getCacheKey(ENDPOINTS.ADMIN.PRODUCTS, filters);

    // Check if we have a valid cached request and not forcing refresh
    if (
      !forceRefresh &&
      this.requestCache.has(cacheKey) &&
      this.isCacheValid(cacheKey)
    ) {
      return this.requestCache.get(cacheKey);
    }

    // If forcing refresh, clear existing cache for this key
    if (forceRefresh) {
      this.requestCache.delete(cacheKey);
      this.cacheTimestamps.delete(cacheKey);
    }

    // Create new request
    const request = axiosClient
      .get<AdminResponse>(ENDPOINTS.ADMIN.PRODUCTS, { params: filters })
      .then((response) => response.data);

    // Cache the request
    this.requestCache.set(cacheKey, request);
    this.cacheTimestamps.set(cacheKey, Date.now());

    // Clear cache after request completes (success or failure)
    request.finally(() => {
      // Keep cache for the duration, don't clear immediately
      setTimeout(() => {
        this.requestCache.delete(cacheKey);
        this.cacheTimestamps.delete(cacheKey);
      }, this.CACHE_DURATION);
    });

    return request;
  }

  /** Unwrap TransformInterceptor payload (handles legacy double-wrapped responses). */
  private unwrapAutoApproveSettingPayload<T extends { enabled?: boolean }>(
    body: unknown,
  ): T {
    const root = body as { data?: unknown; enabled?: boolean } | null | undefined;
    if (!root || typeof root !== "object") {
      return { enabled: false } as T;
    }
    const level1 = root.data as { data?: unknown; enabled?: boolean } | undefined;
    if (level1 && typeof level1 === "object" && typeof level1.enabled === "boolean") {
      return level1 as T;
    }
    if (level1 && typeof level1 === "object" && level1.data && typeof level1.data === "object") {
      return level1.data as T;
    }
    if (typeof root.enabled === "boolean") {
      return root as T;
    }
    return { enabled: false } as T;
  }

  /**
   * Get auto-approve products setting (admin product dashboard).
   */
  async getAutoApproveProductsSetting(): Promise<{
    enabled: boolean;
    updatedAt: string | null;
  }> {
    const response = await axiosClient.get(ENDPOINTS.ADMIN.PRODUCT_AUTO_APPROVE_SETTING);
    return this.unwrapAutoApproveSettingPayload<{
      enabled: boolean;
      updatedAt: string | null;
    }>(response.data);
  }

  /**
   * Enable/disable auto-approve products. Enabling bulk-approves pending listings.
   */
  async setAutoApproveProductsSetting(enabled: boolean): Promise<{
    enabled: boolean;
    approvedCount: number;
    message: string;
  }> {
    const response = await axiosClient.patch(
      ENDPOINTS.ADMIN.PRODUCT_AUTO_APPROVE_SETTING,
      { enabled },
    );
    return this.unwrapAutoApproveSettingPayload<{
      enabled: boolean;
      approvedCount: number;
      message: string;
    }>(response.data);
  }

  /**
   * Get admin product statistics
   */
  async getAdminProductStatistics(
    forceRefresh = false
  ): Promise<AdminResponse> {
    // Clear expired cache entries
    this.clearExpiredCache();

    const cacheKey = this.getCacheKey(ENDPOINTS.ADMIN.PRODUCT_STATISTICS, {});

    // Check if we have a valid cached request and not forcing refresh
    if (
      !forceRefresh &&
      this.requestCache.has(cacheKey) &&
      this.isCacheValid(cacheKey)
    ) {
      return this.requestCache.get(cacheKey);
    }

    // If forcing refresh, clear existing cache for this key
    if (forceRefresh) {
      this.requestCache.delete(cacheKey);
      this.cacheTimestamps.delete(cacheKey);
    }

    // Create new request
    const request = axiosClient
      .get<AdminResponse>(ENDPOINTS.ADMIN.PRODUCT_STATISTICS)
      .then((response) => response.data);

    // Cache the request
    this.requestCache.set(cacheKey, request);
    this.cacheTimestamps.set(cacheKey, Date.now());

    // Clear cache after request completes (success or failure)
    request.finally(() => {
      // Keep cache for the duration, don't clear immediately
      setTimeout(() => {
        this.requestCache.delete(cacheKey);
        this.cacheTimestamps.delete(cacheKey);
      }, this.CACHE_DURATION);
    });

    return request;
  }

  /**
   * Get product by ID
   */
  async getProductById(productId: string): Promise<AdminResponse> {
    const response = await axiosClient.get<AdminResponse>(
      ENDPOINTS.ADMIN.PRODUCT_BY_ID(productId)
    );
    return response.data;
  }

  /**
   * Live vs proposed product for seller pending update (admin)
   */
  async getProductPendingReview(productId: string): Promise<AdminResponse> {
    const response = await axiosClient.get<AdminResponse>(
      ENDPOINTS.ADMIN.PRODUCT_PENDING_REVIEW(productId)
    );
    return response.data;
  }

  /**
   * Seller listing update audit history (admin)
   */
  async getProductSellerUpdateHistory(productId: string): Promise<AdminResponse> {
    const response = await axiosClient.get<AdminResponse>(
      ENDPOINTS.ADMIN.PRODUCT_SELLER_UPDATE_HISTORY(productId)
    );
    return response.data;
  }

  /**
   * Update a product as admin
   */
  async updateProductAsAdmin(
    productId: string,
    productData: any
  ): Promise<AdminResponse> {
    const response = await axiosClient.put<AdminResponse>(
      ENDPOINTS.ADMIN.PRODUCT_BY_ID(productId),
      productData
    );
    return response.data;
  }

  /**
   * Update only product active status (admin)
   * Uses PATCH first to avoid strict full-payload PUT validation.
   */
  async updateProductActiveStatusAsAdmin(
    productId: string,
    isActive: boolean
  ): Promise<AdminResponse> {
    const tryPutIsEnabled = async (): Promise<AdminResponse> => {
      const putFallback = await axiosClient.put<AdminResponse>(
        ENDPOINTS.ADMIN.PRODUCT_BY_ID(productId),
        { isEnabled: isActive }
      );
      return putFallback.data;
    };

    const tryPutIsActive = async (): Promise<AdminResponse> => {
      const putFallback = await axiosClient.put<AdminResponse>(
        ENDPOINTS.ADMIN.PRODUCT_BY_ID(productId),
        { isActive }
      );
      return putFallback.data;
    };

    // Use admin PUT endpoint first (backend-supported), then compatibility fallback.
    const strategies = [tryPutIsEnabled, tryPutIsActive];

    let lastResponse: AdminResponse = {
      success: false,
      data: null,
      message: "Failed to update active status",
    };

    for (const strategy of strategies) {
      try {
        const result = await strategy();
        lastResponse = result;
        if (result?.success) return result;
      } catch {
        // Continue to next fallback strategy
      }
    }

    return lastResponse;
  }

  /**
   * Assign product to seller
   */
  async assignProductToSeller(
    productId: string,
    sellerData: any
  ): Promise<AdminResponse> {
    const response = await axiosClient.post<AdminResponse>(
      ENDPOINTS.ADMIN.ASSIGN_PRODUCT(productId),
      sellerData
    );
    return response.data;
  }

  /**
   * Approve a product
   */
  async approveProduct(productId: string): Promise<AdminResponse> {
    const response = await axiosClient.post<AdminResponse>(
      ENDPOINTS.ADMIN.APPROVE_PRODUCT(productId)
    );
    return response.data;
  }

  /**
   * Reject a product
   */
  async rejectProduct(
    productId: string,
    reason?: string
  ): Promise<AdminResponse> {
    const response = await axiosClient.post<AdminResponse>(
      ENDPOINTS.ADMIN.REJECT_PRODUCT(productId),
      { reason }
    );
    return response.data;
  }

  /**
   * Duplicate a product
   */
  async duplicateProduct(productId: string): Promise<AdminResponse> {
    const response = await axiosClient.post<AdminResponse>(
      ENDPOINTS.ADMIN.DUPLICATE_PRODUCT(productId)
    );
    return response.data;
  }

  /**
   * Delete a product (soft delete)
   */
  async deleteProduct(productId: string): Promise<AdminResponse> {
    const response = await axiosClient.delete<AdminResponse>(
      ENDPOINTS.ADMIN.PRODUCT_BY_ID(productId)
    );
    return response.data;
  }

  /**
   * Get available sellers for assignment
   */
  async getAvailableSellers(companyId?: string): Promise<AdminResponse> {
    const response = await axiosClient.get<AdminResponse>(
      ENDPOINTS.ADMIN.AVAILABLE_SELLERS,
      { params: { companyId } }
    );
    return response.data;
  }

  async saveProductAsDraft(productData: any): Promise<AdminResponse> {
    const response = await axiosClient.post<AdminResponse>(
      ENDPOINTS.ADMIN.SAVE_DRAFT,
      productData
    );
    return response.data;
  }

  async publishProduct(productData: any): Promise<AdminResponse> {
    const response = await axiosClient.post<AdminResponse>(
      ENDPOINTS.ADMIN.PUBLISH,
      productData
    );
    return response.data;
  }

  async updateProductToDraft(
    productId: string,
    productData: any
  ): Promise<AdminResponse> {
    const response = await axiosClient.put<AdminResponse>(
      ENDPOINTS.ADMIN.UPDATE_TO_DRAFT(productId),
      productData
    );
    return response.data;
  }

  async updateProductToPublished(
    productId: string,
    productData: any
  ): Promise<AdminResponse> {
    const response = await axiosClient.put<AdminResponse>(
      ENDPOINTS.ADMIN.UPDATE_TO_PUBLISHED(productId),
      productData
    );
    return response.data;
  }

  /**
   * Get all categories — always hits the network (no in-memory cache).
   * Unique `_t` query avoids HTTP caches without extra request headers (those trigger CORS preflight on cross-origin GET).
   */
  async getCategories(
    hierarchy = false,
    _forceRefresh = false,
    maxDepth?: number,
  ): Promise<any[]> {
    const endpoint = ENDPOINTS.CATEGORIES.BASE;
    const bust = `_t=${Date.now()}`;
    const resolvedDepth =
      hierarchy && (maxDepth !== undefined && maxDepth > 0 ? maxDepth : 2);
    const depthParam =
      hierarchy && resolvedDepth !== undefined
        ? `&maxDepth=${resolvedDepth}`
        : "";
    const url = hierarchy
      ? `${endpoint}?hierarchy=true${depthParam}&${bust}`
      : `${endpoint}?${bust}`;

    const response = await axiosClient.get<{
      success: boolean;
      data: any[];
    }>(url);
    return response.data.data;
  }

  /**
   * Create a new category
   */
  async createCategory(categoryData: any): Promise<AdminResponse> {
    const response = await axiosClient.post<AdminResponse>(
      ENDPOINTS.ADMIN.CATEGORIES,
      categoryData
    );
    // Invalidate cache after creation
    this.invalidateCategoriesCache();
    return response.data;
  }

  /**
   * Update a category
   */
  async updateCategory(
    categoryId: string,
    categoryData: any
  ): Promise<AdminResponse> {
    const response = await axiosClient.put<AdminResponse>(
      ENDPOINTS.ADMIN.CATEGORY_BY_ID(categoryId),
      categoryData
    );
    // Invalidate cache after update
    this.invalidateCategoriesCache();
    return response.data;
  }

  /**
   * Delete a category
   */
  async deleteCategory(categoryId: string): Promise<AdminResponse> {
    const response = await axiosClient.delete<AdminResponse>(
      ENDPOINTS.ADMIN.CATEGORY_BY_ID(categoryId)
    );
    // Invalidate cache after deletion
    this.invalidateCategoriesCache();
    return response.data;
  }

  /**
   * Toggle category featured status
   */
  async toggleCategoryFeatured(categoryId: string): Promise<AdminResponse> {
    const response = await axiosClient.patch<AdminResponse>(
      `${ENDPOINTS.ADMIN.CATEGORY_BY_ID(categoryId)}/toggle-featured`
    );
    // Invalidate cache after toggle
    this.invalidateCategoriesCache();
    return response.data;
  }

  /**
   * Toggle category active status
   */
  async toggleCategoryActive(categoryId: string): Promise<AdminResponse> {
    const response = await axiosClient.patch<AdminResponse>(
      `${ENDPOINTS.ADMIN.CATEGORY_BY_ID(categoryId)}/toggle-active`
    );
    // Invalidate cache after toggle
    this.invalidateCategoriesCache();
    return response.data;
  }

  /**
   * Media Management APIs
   */
  async uploadMedia(
    file: File,
    folder?: string,
    libraryOnly = false
  ): Promise<{ id: string; url: string; thumbnailUrl?: string }> {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const params: Record<string, string> = {};
      if (folder?.trim()) {
        params.folder = folder.trim();
      }
      if (libraryOnly) {
        params.libraryOnly = "true";
      }
      const response = await axiosClient.post<AdminResponse>(
        ENDPOINTS.ADMIN.MEDIA.UPLOAD,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          params: Object.keys(params).length > 0 ? params : undefined,
          timeout: 120000,
        }
      );
      // TransformInterceptor wraps the controller body; upload controller also returns { success, data }.
      const res = response.data as any;
      const outer = res?.data;
      const result = outer?.data ?? outer;
      if (result && typeof result === "object" && "url" in result) {
        return {
          id: (result as any).id ?? "",
          url: (result as any).url ?? "",
          thumbnailUrl: (result as any).thumbnailUrl,
        };
      }
      throw new Error("Upload failed: invalid response");
    } catch (error) {
      const reason = extractApiErrorMessage(
        error,
        "Media upload failed. Please try again."
      );
      showUploadErrorAlert(reason);
      throw new Error(reason);
    }
  }

  async getMedia(
    id: string
  ): Promise<{ url: string; thumbnailUrl?: string; metadata: any }> {
    const response = await axiosClient.get<AdminResponse>(
      ENDPOINTS.ADMIN.MEDIA.BY_ID(id)
    );
    const res = response.data as any;
    const outer = res?.data;
    const result = outer?.data ?? outer;
    if (result && typeof result === "object" && "url" in result) {
      return result as { url: string; thumbnailUrl?: string; metadata: any };
    }
    throw new Error("Get media failed: invalid response");
  }

  /**
   * Download media binary via API (forces save; cross-origin Spaces URLs ignore <a download>).
   */
  async downloadMediaFile(id: string, suggestedFilename: string): Promise<void> {
    const response = await axiosClient.get(ENDPOINTS.ADMIN.MEDIA.DOWNLOAD(id), {
      responseType: "blob",
    });
    const blob = response.data as Blob;
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = suggestedFilename;
    a.rel = "noopener";
    a.click();
    URL.revokeObjectURL(objectUrl);
  }

  async listMedia(
    page = 1,
    limit = 20,
    sellerId?: string,
    search?: string,
    folder?: string,
    libraryOnly = false,
    mediaType?: "image" | "pdf"
  ): Promise<{ data: any[];
    total: number;
    page: number;
    limit: number;
  }> {
    const params: any = { page, limit };
    if (sellerId) {
      params.sellerId = sellerId;
    }
    const q = search?.trim();
    if (q) {
      params.search = q;
    }
    const folderSlug = folder?.trim();
    if (folderSlug) {
      params.folder = folderSlug;
    }
    if (libraryOnly) {
      params.libraryOnly = "true";
    }
    if (mediaType === "image" || mediaType === "pdf") {
      params.mediaType = mediaType;
    }
    const response = await axiosClient.get<AdminResponse>(
      ENDPOINTS.ADMIN.MEDIA.LIST,
      { params }
    );
    // The API returns { success: true, data: { data: [], total: 0, page: 1, limit: 20 } }
    return response.data.data;
  }

  async deleteMedia(id: string): Promise<void> {
    await axiosClient.delete<AdminResponse>(ENDPOINTS.ADMIN.MEDIA.DELETE(id));
  }

  async deleteMultipleMedia(ids: string[]): Promise<string[]> {
    const normalizedIds = Array.from(
      new Set(
        (ids || [])
          .map((id) => (typeof id === "string" ? id.trim() : ""))
          .filter(Boolean)
      )
    );
    if (normalizedIds.length === 0) {
      return [];
    }

    const deleteResults = await Promise.allSettled(
      normalizedIds.map(async (id) => {
        await this.deleteMedia(id);
        return id;
      })
    );
    return deleteResults
      .filter(
        (result): result is PromiseFulfilledResult<string> =>
          result.status === "fulfilled"
      )
      .map((result) => result.value);
  }

  async listMediaFolders(
    sellerId?: string,
    libraryOnly = false
  ): Promise<Array<{ name: string; slug: string; count: number }>> {
    const params: Record<string, string> = {};
    if (sellerId) {
      params.sellerId = sellerId;
    }
    if (libraryOnly) {
      params.libraryOnly = "true";
    }
    const response = await axiosClient.get<AdminResponse>(
      ENDPOINTS.ADMIN.MEDIA.FOLDERS,
      { params: Object.keys(params).length > 0 ? params : undefined }
    );
    const payload = response.data?.data;
    const folders = payload?.folders ?? payload?.data?.folders ?? [];
    return Array.isArray(folders) ? folders : [];
  }

  async createMediaFolder(
    name: string
  ): Promise<{ name: string; slug: string; count: number }> {
    const response = await axiosClient.post<AdminResponse>(
      ENDPOINTS.ADMIN.MEDIA.FOLDERS,
      { name }
    );
    const payload = response.data?.data;
    const folder = payload?.data ?? payload;
    if (folder && typeof folder === "object" && "slug" in folder) {
      return folder as { name: string; slug: string; count: number };
    }
    throw new Error("Create folder failed: invalid response");
  }

  async updateMediaFolder(
    slug: string,
    name: string
  ): Promise<{ name: string; slug: string; count: number }> {
    const response = await axiosClient.patch<AdminResponse>(
      ENDPOINTS.ADMIN.MEDIA.FOLDER_BY_SLUG(slug),
      { name }
    );
    const payload = response.data?.data;
    const folder = payload?.data ?? payload;
    if (folder && typeof folder === "object" && "slug" in folder) {
      return folder as { name: string; slug: string; count: number };
    }
    throw new Error("Update folder failed: invalid response");
  }

  async deleteMediaFolder(slug: string): Promise<{ movedCount: number }> {
    const response = await axiosClient.delete<AdminResponse>(
      ENDPOINTS.ADMIN.MEDIA.FOLDER_BY_SLUG(slug)
    );
    const payload = response.data?.data;
    return (payload?.data ?? payload ?? { movedCount: 0 }) as {
      movedCount: number;
    };
  }

  async moveMediaToFolder(
    id: string,
    folder: string
  ): Promise<{ id: string; folder: string }> {
    const response = await axiosClient.patch<AdminResponse>(
      ENDPOINTS.ADMIN.MEDIA.MOVE_TO_FOLDER(id),
      { folder }
    );
    const payload = response.data?.data;
    const result = payload?.data ?? payload;
    if (result && typeof result === "object" && "folder" in result) {
      return result as { id: string; folder: string };
    }
    throw new Error("Move media failed: invalid response");
  }

  async moveMultipleMediaToFolder(
    ids: string[],
    folder: string
  ): Promise<Array<{ id: string; folder: string }>> {
    const normalizedIds = Array.from(
      new Set(
        (ids || [])
          .map((id) => (typeof id === "string" ? id.trim() : ""))
          .filter(Boolean)
      )
    );
    if (normalizedIds.length === 0) {
      return [];
    }

    const moveResults = await Promise.allSettled(
      normalizedIds.map((id) => this.moveMediaToFolder(id, folder))
    );
    return moveResults
      .filter(
        (result): result is PromiseFulfilledResult<{ id: string; folder: string }> =>
          result.status === "fulfilled"
      )
      .map((result) => result.value);
  }

  /**
   * Get all sellers with optional filtering
   */
  async getSellers(queryParams?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    country?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<{
    sellers: PendingUser[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const response = await axiosClient.get<AdminResponse>(
      ENDPOINTS.ADMIN.SELLERS,
      { params: queryParams }
    );
    return response.data.data;
  }

  /**
   * Update seller status
   */
  async updateSellerStatus(
    sellerId: string,
    status: string,
    reason?: string
  ): Promise<PendingUser> {
    const response = await axiosClient.patch<AdminResponse>(
      ENDPOINTS.ADMIN.SELLER_STATUS(sellerId),
      { status, reason }
    );
    return response.data.data;
  }

  /**
   * Get reports metrics
   */
  async getReportsMetrics(dateRange: string = "30days"): Promise<{
    activeSuppliers: number;
    activeBuyers: number;
    changes: {
      suppliers: { value: number; isPositive: boolean };
      buyers: { value: number; isPositive: boolean };
    };
    timestamp: string;
  }> {
    const response = await axiosClient.get<{
      success: boolean;
      data: {
        activeSuppliers: number;
        activeBuyers: number;
        changes: {
          suppliers: { value: number; isPositive: boolean };
          buyers: { value: number; isPositive: boolean };
        };
        timestamp: string;
      };
    }>(ENDPOINTS.ADMIN.REPORTS.METRICS, {
      params: { dateRange },
    });
    return response.data.data;
  }

  /**
   * Get sales trend
   */
  async getSalesTrend(dateRange: string = "30days"): Promise<{
    data: Array<{
      period: string;
      sales: number;
      orders: number;
    }>;
    timestamp: string;
  }> {
    const response = await axiosClient.get<{
      success: boolean;
      data: {
        data: Array<{
          period: string;
          sales: number;
          orders: number;
        }>;
        timestamp: string;
      };
    }>(ENDPOINTS.ADMIN.REPORTS.SALES_TREND, {
      params: { dateRange },
    });
    return response.data.data;
  }

  /**
   * Get user growth
   */
  async getUserGrowth(dateRange: string = "30days"): Promise<{
    data: Array<{
      period: string;
      buyers: number;
      suppliers: number;
    }>;
    timestamp: string;
  }> {
    const response = await axiosClient.get<{
      success: boolean;
      data: {
        data: Array<{
          period: string;
          buyers: number;
          suppliers: number;
        }>;
        timestamp: string;
      };
    }>(ENDPOINTS.ADMIN.REPORTS.USER_GROWTH, {
      params: { dateRange },
    });
    return response.data.data;
  }

  /**
   * Get category distribution
   */
  async getCategoryDistribution(): Promise<{
    data: Array<{
      name: string;
      value: number;
      color: string;
    }>;
    timestamp: string;
  }> {
    const response = await axiosClient.get<{
      success: boolean;
      data: {
        data: Array<{
          name: string;
          value: number;
          color: string;
        }>;
        timestamp: string;
      };
    }>(ENDPOINTS.ADMIN.REPORTS.CATEGORY_DISTRIBUTION);
    return response.data.data;
  }

  /**
   * Get generated reports
   */
  async getGeneratedReports(): Promise<{
    data: Array<{
      id: string;
      name: string;
      type: string;
      createdDate: string;
      status: "ready" | "generating" | "error";
    }>;
    timestamp: string;
  }> {
    const response = await axiosClient.get<{
      success: boolean;
      data: {
        data: Array<{
          id: string;
          name: string;
          type: string;
          createdDate: string;
          status: "ready" | "generating" | "error";
        }>;
        timestamp: string;
      };
    }>(ENDPOINTS.ADMIN.REPORTS.GENERATED);
    return response.data.data;
  }

  /**
   * Export reports
   */
  async exportReports(
    dateRange: string = "30days",
    format: "csv" | "pdf" = "csv"
  ): Promise<Blob> {
    const response = await axiosClient.get(ENDPOINTS.ADMIN.REPORTS.EXPORT, {
      params: { dateRange, format },
      responseType: "blob",
    });
    return response.data;
  }

  /**
   * Get all storefront enquiries (admin only). Paginated.
   */
  async getStorefrontInquiriesAll(
    status?: "PENDING" | "RESPONDED" | "CLOSED",
    page = 1,
    limit = 10,
    search?: string
  ): Promise<{ inquiries: any[]; total: number; page: number; limit: number }> {
    const params: Record<string, string | number> = { page, limit };
    if (status) params.status = status;
    if (search?.trim()) params.search = search.trim();
    const response = await axiosClient.get(ENDPOINTS.STOREFRONT.INQUIRIES_ALL, {
      params,
    });
    const data = response.data;
    const payload = data?.data ?? data;
    const list = Array.isArray(payload?.inquiries)
      ? payload.inquiries
      : Array.isArray(payload)
        ? payload
        : [];
    return {
      inquiries: list,
      total: Number(payload?.total ?? data?.total ?? 0) || 0,
      page: Number(payload?.page ?? data?.page ?? page) || page,
      limit: Number(payload?.limit ?? data?.limit ?? limit) || limit,
    };
  }
}

// Export singleton instance
export const adminService = new AdminService();
